import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Employee, WeeklySchedule, Schedule } from '../../types/weeklySchedule';

/**
 * Modelo de datos para impresión del calendario semanal
 */
export interface PrintCellData {
    type: 'empty' | 'shift' | 'rest';
    code?: string;          // Código corto (ej: "TM", "DL")
    hours?: string;         // Horas exactas (ej: "08:00-14:00")
    name?: string;          // Nombre del turno
    color?: string;         // Color del turno
}

export interface PrintEmployeeData {
    id: string;
    name: string;
    department?: string;
    cells: PrintCellData[];  // 7 celdas (L-D)
    totalHours: number;
}

export interface PrintPageData {
    pageNumber: number;
    totalPages: number;
    employees: PrintEmployeeData[];
    employeeRange: string;    // ej: "1-30", "31-60"
    showDepartment: boolean;
    showTotal: boolean;
}

export interface PrintCalendarData {
    title: string;
    weekStart: Date;
    weekEnd: Date;
    department?: string;
    printDate: Date;
    pages: PrintPageData[];
    legend: Map<string, string>;  // código -> descripción
}

export interface PrintOptions {
    mode: 'compact' | 'detailed';
    showExactHours: boolean;
    showLegend: boolean;
    showDepartment: boolean;
    showTotal: boolean;
}

/**
 * Genera un código corto para un turno o día de descanso
 */
function generateShiftCode(schedule: Schedule, _mode: 'compact' | 'detailed'): string {
    // Si el nombre del turno es corto (menos de 4 caracteres), úsalo
    if (schedule.name.length <= 4) {
        return schedule.name.toUpperCase();
    }

    // Generar código basado en las iniciales (ej: "Turno Mañana" -> "TM")
    const initials = schedule.name
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase())
        .join('');

    if (initials.length >= 2 && initials.length <= 4) {
        return initials;
    }

    // Si es muy largo, acortar
    return schedule.name.substring(0, 3).toUpperCase();
}

/**
 * Genera texto de horas corto o largo según modo
 */
function formatShiftTime(
    schedule: Schedule,
    showExactHours: boolean,
    mode: 'compact' | 'detailed'
): string {
    if (!showExactHours) {
        return '';
    }

    // Formato corto: 08-14
    const startShort = schedule.startTime.substring(0, 5).replace(':', '');
    const endShort = schedule.endTime.substring(0, 5).replace(':', '');

    if (mode === 'compact') {
        return `${startShort}-${endShort}`;
    }

    // Formato largo: 08:00 - 14:00
    return `${schedule.startTime.substring(0, 5)}-${schedule.endTime.substring(0, 5)}`;
}

/**
 * Transforma los datos del calendario en un modelo de impresión
 */
export function transformCalendarToPrintData(
    employees: Employee[],
    weeklySchedules: WeeklySchedule[],
    _schedules: Schedule[],
    weekStart: Date,
    options: PrintOptions,
    departmentName?: string
): PrintCalendarData {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const printDate = new Date();
    const printEmployeeData: PrintEmployeeData[] = [];
    const legend = new Map<string, string>();

    // Procesar cada empleado
    employees.forEach(employee => {
        const cells: PrintCellData[] = [];
        let totalHours = 0;

        // Para cada día de la semana (0-6)
        for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
            const daySchedules = weeklySchedules.filter(
                ws => ws.employeeId === employee.id && ws.dayOfWeek === dayOfWeek
            );

            if (daySchedules.length === 0) {
                cells.push({ type: 'empty' });
            } else if (daySchedules.some(ws => ws.scheduleId === null)) {
                // Día de descanso
                const code = options.mode === 'compact' ? 'DL' : 'Descanso';
                cells.push({
                    type: 'rest',
                    code,
                    name: 'Día libre',
                    color: '#f3f4f6'
                });

                if (options.showLegend) {
                    legend.set(code, 'Día libre');
                }
            } else {
                // Turnos asignados
                const shifts = daySchedules
                    .filter(ws => ws.schedule)
                    .map(ws => ws.schedule!);

                if (shifts.length === 1) {
                    const schedule = shifts[0];
                    const code = generateShiftCode(schedule, options.mode);
                    const hours = formatShiftTime(schedule, options.showExactHours, options.mode);

                    // Calcular horas
                    const [startH, startM] = schedule.startTime.split(':').map(Number);
                    const [endH, endM] = schedule.endTime.split(':').map(Number);
                    let hoursDiff = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
                    if (hoursDiff < 0) hoursDiff += 24; // Turno nocturno
                    totalHours += hoursDiff;

                    cells.push({
                        type: 'shift',
                        code,
                        hours: options.showExactHours ? hours : undefined,
                        name: schedule.name,
                        color: schedule.color || '#3B82F6'
                    });

                    if (options.showLegend && !legend.has(code)) {
                        legend.set(code, schedule.name);
                    }
                } else if (shifts.length > 1) {
                    // Múltiples turnos en un día
                    const codes = shifts.map(s => generateShiftCode(s, options.mode)).join('/');
                    const allHours = shifts
                        .map(s => formatShiftTime(s, options.showExactHours, options.mode))
                        .filter(h => h)
                        .join(' / ');

                    shifts.forEach(schedule => {
                        const [startH, startM] = schedule.startTime.split(':').map(Number);
                        const [endH, endM] = schedule.endTime.split(':').map(Number);
                        let hoursDiff = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
                        if (hoursDiff < 0) hoursDiff += 24;
                        totalHours += hoursDiff;

                        // Añadir a leyenda si no existe
                        if (options.showLegend) {
                            const code = generateShiftCode(schedule, options.mode);
                            if (!legend.has(code)) {
                                legend.set(code, schedule.name);
                            }
                        }
                    });

                    cells.push({
                        type: 'shift',
                        code: codes,
                        hours: allHours || undefined,
                        name: shifts.map(s => s.name).join(', '),
                        color: '#dbeafe'
                    });
                }
            }
        }

        printEmployeeData.push({
            id: employee.id,
            name: employee.name,
            department: options.showDepartment ? employee.departmentModel?.name : undefined,
            cells,
            totalHours
        });
    });

    // Dividir en páginas de 30 empleados para modo compacto
    const employeesPerPage = options.mode === 'compact' ? 30 : 15;
    const totalPages = Math.ceil(printEmployeeData.length / employeesPerPage);
    const pages: PrintPageData[] = [];

    for (let i = 0; i < totalPages; i++) {
        const startIndex = i * employeesPerPage;
        const endIndex = Math.min(startIndex + employeesPerPage, printEmployeeData.length);
        const pageEmployees = printEmployeeData.slice(startIndex, endIndex);

        pages.push({
            pageNumber: i + 1,
            totalPages,
            employees: pageEmployees,
            employeeRange: `${startIndex + 1}-${endIndex}`,
            showDepartment: options.showDepartment,
            showTotal: options.showTotal
        });
    }

    return {
        title: 'Calendario Semanal de Horarios',
        weekStart,
        weekEnd,
        department: departmentName,
        printDate,
        pages,
        legend
    };
}

/**
 * Genera el HTML de impresión
 */
export function generatePrintHTML(data: PrintCalendarData): string {
    const weekStartStr = format(data.weekStart, 'dd MMM yyyy', { locale: es });
    const weekEndStr = format(data.weekEnd, 'dd MMM yyyy', { locale: es });
    const printDateStr = format(data.printDate, 'dd/MM/yyyy HH:mm');

    const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const daysDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(data.weekStart);
        date.setDate(date.getDate() + i);
        return format(date, 'dd/MM');
    });

    let pagesHTML = '';

    data.pages.forEach(page => {
        const showDept = page.showDepartment;
        const showTotal = page.showTotal;

        const rowsHTML = page.employees.map(emp => {
            const cellsHTML = emp.cells.map(cell => {
                if (cell.type === 'empty') {
                    return '<td class="cell-empty">—</td>';
                }

                const cellClass = cell.type === 'rest' ? 'cell-rest' : 'cell-shift';
                const contentHTML = `
                    <div class="cell-content">
                        <span class="cell-code">${cell.code || ''}</span>
                        ${cell.hours ? `<span class="cell-hours">${cell.hours}</span>` : ''}
                    </div>
                `;

                return `<td class="${cellClass}">${contentHTML}</td>`;
            }).join('');

            const deptCell = showDept && emp.department
                ? `<td class="cell-dept">${emp.department}</td>`
                : (showDept ? '<td class="cell-dept">—</td>' : '');

            const totalCell = showTotal
                ? `<td class="cell-total">${emp.totalHours > 0 ? emp.totalHours.toFixed(1) + 'h' : '—'}</td>`
                : '';

            return `
                <tr>
                    <td class="cell-employee">${emp.name}</td>
                    ${deptCell}
                    ${cellsHTML}
                    ${totalCell}
                </tr>
            `;
        }).join('');

        const footerLegendHTML = data.legend.size > 0
            ? `<div class="legend">
                ${Array.from(data.legend.entries()).map(([code, desc]) => `
                    <span class="legend-item"><strong>${code}</strong>: ${desc}</span>
                `).join(' • ')}
            </div>`
            : '<!-- No hay leyenda para mostrar -->';

        const deptHeader = showDept ? '<th class="th-dept">Departamento</th>' : '';
        const totalHeader = showTotal ? '<th class="th-total">Total</th>' : '';

        pagesHTML += `
            <div class="print-page">
                <div class="print-header">
                    <h1>${data.title}</h1>
                    <p>Semana del ${weekStartStr} al ${weekEndStr}</p>
                    ${data.department ? `<p>Departamento: <strong>${data.department}</strong></p>` : ''}
                    <p>Fecha de impresión: ${printDateStr}</p>
                </div>

                <table class="print-table ${showDept ? 'with-dept' : ''} ${showTotal ? 'with-total' : ''}">
                    <thead>
                        <tr>
                            <th class="th-employee">Empleado</th>
                            ${deptHeader}
                            ${daysOfWeek.map((day, i) => `
                                <th class="th-day">
                                    <div>${day}</div>
                                    <div class="th-date">${daysDates[i]}</div>
                                </th>
                            `).join('')}
                            ${totalHeader}
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHTML}
                    </tbody>
                </table>

                ${footerLegendHTML}

                <div class="print-footer">
                    <span>Empleados: ${page.employeeRange}</span>
                    <span class="page-number">Página ${page.pageNumber} de ${page.totalPages}</span>
                </div>
            </div>
        `;
    });

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Calendario Semanal - ${weekStartStr}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        @page {
            size: A4 landscape;
            margin: 6mm;
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
            font-size: 7pt;
            color: #000;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        .print-page {
            page-break-after: always;
            page-break-inside: avoid;
            break-after: always;
            break-inside: avoid;
        }

        .print-page:last-child {
            page-break-after: auto;
            break-after: auto;
        }

        .print-header {
            border-bottom: 2px solid #000;
            padding-bottom: 5px;
            margin-bottom: 8px;
            page-break-after: avoid;
            break-after: avoid;
        }

        .print-header h1 {
            font-size: 12pt;
            font-weight: 700;
            text-align: center;
            margin-bottom: 3px;
        }

        .print-header p {
            font-size: 8pt;
            text-align: center;
            margin-bottom: 2px;
            color: #333;
        }

        .print-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            page-break-inside: avoid;
            break-inside: avoid;
            font-size: inherit;
        }

        .print-table thead {
            background-color: #f0f0f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            page-break-after: avoid;
            break-after: avoid;
        }

        .print-table th,
        .print-table td {
            border: 1px solid #000 !important;
            padding: 2px 3px !important;
            page-break-inside: avoid;
            break-inside: avoid;
            vertical-align: top !important;
            text-align: center;
            font-size: inherit;
        }

        .print-table th {
            background-color: #f0f0f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            font-weight: 700;
            height: 10mm;
        }

        .print-table tbody tr {
            page-break-inside: avoid;
            break-inside: avoid;
            height: 5mm;
        }

        .th-employee {
            width: 40mm;
            text-align: left;
        }

        .th-dept {
            width: 30mm;
            text-align: left;
        }

        /* Ancho de días cuando hay columna de departamento */
        .print-table.with-dept .th-day {
            width: calc((100% - 70mm - 15mm) / 7);
        }

        /* Ancho de días cuando NO hay columna de departamento */
        .print-table:not(.with-dept) .th-day {
            width: calc((100% - 40mm - 15mm) / 7);
        }

        .th-day .th-date {
            font-size: 6pt;
            font-weight: 400;
            margin-top: 1px;
        }

        .th-total {
            width: 15mm;
            background-color: #dbeafe !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        /* Ancho de días sin columna de departamento PERO con total */
        .print-table:not(.with-dept).with-total .th-day {
            width: calc((100% - 40mm - 15mm) / 7);
        }

        /* Ancho de días sin columna de departamento Y sin total */
        .print-table:not(.with-dept):not(.with-total) .th-day {
            width: calc((100% - 40mm) / 7);
        }

        /* Ancho de días con departamento PERO sin total */
        .print-table.with-dept:not(.with-total) .th-day {
            width: calc((100% - 70mm) / 7);
        }

        .cell-employee {
            font-weight: 600;
            text-align: left;
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            font-size: 6.5pt;
        }

        .cell-dept {
            text-align: left;
            font-size: 6pt;
            color: #555;
        }

        .cell-empty {
            color: #ccc;
        }

        .cell-shift,
        .cell-rest {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border-radius: 2px;
        }

        .cell-shift {
            background-color: #dbeafe !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        .cell-rest {
            background-color: #f3f4f6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color: #666;
        }

        .cell-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.5mm;
        }

        .cell-code {
            font-weight: 600;
            font-size: 7pt;
        }

        .cell-hours {
            font-size: 6pt;
            color: #555;
        }

        .cell-total {
            font-weight: 700;
            background-color: #dbeafe !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        .legend {
            margin: 10mm 0 5mm 0;
            padding: 5mm 8mm;
            background-color: #ffffff !important;
            border: 2px solid #000 !important;
            border-radius: 4px;
            font-size: 7pt;
            font-weight: 500;
            text-align: center;
            page-break-inside: avoid;
            break-inside: avoid;
            color: #000 !important;
            line-height: 1.5;
            display: block !important;
            width: 100% !important;
        }

        .legend-item {
            margin: 0 5mm;
            color: #000 !important;
            display: inline-block;
            font-weight: 600;
            white-space: nowrap;
        }

        .legend-item strong {
            color: #000 !important;
            background-color: #e5e7eb !important;
            padding: 1px 4px;
            border-radius: 2px;
            text-decoration: none;
        }

        .print-footer {
            margin-top: 5px;
            padding-top: 2mm;
            border-top: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 6pt;
            color: #666;
            page-break-after: avoid;
            break-after: avoid;
        }

        .page-number {
            font-weight: 600;
        }

        button,
        .no-print {
            display: none !important;
        }

        @media print {
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    ${pagesHTML}
</body>
</html>
`;
}

/**
 * Genera datos mock para pruebas de impresión
 */
export function generateMockPrintData(count: number = 30): {
    employees: Employee[];
    weeklySchedules: WeeklySchedule[];
    schedules: Schedule[];
} {
    const employees: Employee[] = [];
    const weeklySchedules: WeeklySchedule[] = [];
    const schedules: Schedule[] = [
        { id: 's1', name: 'Mañana', startTime: '08:00', endTime: '14:00', color: '#3B82F6', active: true },
        { id: 's2', name: 'Tarde', startTime: '14:00', endTime: '20:00', color: '#10B981', active: true },
        { id: 's3', name: 'Noche', startTime: '20:00', endTime: '02:00', color: '#8B5CF6', active: true },
        { id: 's4', name: 'Mñana Completo', startTime: '08:00', endTime: '18:00', color: '#F59E0B', active: true },
        { id: 's5', name: 'Tarde Completo', startTime: '12:00', endTime: '20:00', color: '#EF4444', active: true },
    ];

    const departments = ['Ventas', 'Marketing', 'IT', 'RRHH', 'Finanzas'];

    for (let i = 1; i <= count; i++) {
        const employee: Employee = {
            id: `emp${i}`,
            dni: `12345678${i.toString().padStart(2, '0')}`,
            name: `Empleado ${i}`,
            departmentModel: {
                id: `dept${(i % 5) + 1}`,
                name: departments[i % 5],
                active: true,
                companyId: 'mock',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            },
            active: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        employees.push(employee);

        // Asignar horarios aleatorios para cada día
        for (let day = 0; day < 7; day++) {
            const rand = Math.random();
            const scheduleIndex = Math.floor(rand * (schedules.length + 1));

            if (scheduleIndex < schedules.length && day !== 5) { // 5 = sábado, más días libres
                weeklySchedules.push({
                    id: `ws${i}_${day}`,
                    companyId: 'mock',
                    employeeId: employee.id,
                    weekStart: new Date().toISOString(),
                    dayOfWeek: day,
                    scheduleId: schedules[scheduleIndex].id,
                    active: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    schedule: schedules[scheduleIndex]
                });
            } else if (scheduleIndex === schedules.length) {
                // Día de descanso - usar string vacío ya que scheduleId no puede ser null
                weeklySchedules.push({
                    id: `ws${i}_${day}_rest`,
                    companyId: 'mock',
                    employeeId: employee.id,
                    weekStart: new Date().toISOString(),
                    dayOfWeek: day,
                    scheduleId: '',
                    active: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            }
        }
    }

    return { employees, weeklySchedules, schedules };
}
